import { useEffect, useMemo, useRef, useState } from 'react';

interface DateTimePickerProps {
  /** Y-m-d H:i:s 或空字符串 */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** 是否允许清空，默认 false（下单时间一般必填） */
  clearable?: boolean;
}

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function toDateValue(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseDateTimeValue(value: string): Date | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(raw);
  if (!m) return null;
  const date = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0),
    Number(m[6] ?? 0),
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTimeValue(d: Date) {
  return `${toDateValue(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

function formatDisplayText(value: string) {
  const d = parseDateTimeValue(value);
  if (!d) return '';
  return `${toDateValue(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, delta: number) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function buildMonthCells(viewMonth: Date) {
  const first = startOfMonth(viewMonth);
  const startOffset = first.getDay();
  const cells: Array<{ date: Date; inMonth: boolean }> = [];
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startOffset);

  for (let i = 0; i < 42; i++) {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    cells.push({
      date,
      inMonth: date.getMonth() === viewMonth.getMonth(),
    });
  }
  return cells;
}

function clampInt(n: number, min: number, max: number) {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

/**
 * 日期时间选择器，样式对齐 DateRangePicker / MonthPicker
 */
export default function DateTimePicker({
  value,
  onChange,
  placeholder = '选择日期时间',
  className = '',
  disabled = false,
  clearable = false,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const parsed = useMemo(() => parseDateTimeValue(value), [value]);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(parsed || new Date()));
  const [draftDate, setDraftDate] = useState(() => (parsed ? toDateValue(parsed) : ''));
  const [draftHour, setDraftHour] = useState(() => (parsed ? pad2(parsed.getHours()) : '00'));
  const [draftMinute, setDraftMinute] = useState(() =>
    parsed ? pad2(parsed.getMinutes()) : '00',
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const d = parseDateTimeValue(value) || new Date();
    setViewMonth(startOfMonth(d));
    setDraftDate(toDateValue(d));
    setDraftHour(pad2(d.getHours()));
    setDraftMinute(pad2(d.getMinutes()));
  }, [open, value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);
  const draftDay = useMemo(() => {
    if (!draftDate) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(draftDate);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }, [draftDate]);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const displayText = useMemo(() => formatDisplayText(value), [value]);
  const hasValue = !!value;
  const monthLabel = `${viewMonth.getFullYear()}年${viewMonth.getMonth() + 1}月`;

  const composeValue = (dateStr: string, hour: string, minute: string) => {
    if (!dateStr) return '';
    const h = pad2(clampInt(Number(hour), 0, 23));
    const mi = pad2(clampInt(Number(minute), 0, 59));
    const keepSec = parsed && toDateValue(parsed) === dateStr ? pad2(parsed.getSeconds()) : '00';
    return `${dateStr} ${h}:${mi}:${keepSec}`;
  };

  const commit = (dateStr: string, hour: string, minute: string) => {
    const next = composeValue(dateStr, hour, minute);
    if (next) onChange(next);
  };

  const handleDayClick = (day: Date) => {
    const dateStr = toDateValue(day);
    setDraftDate(dateStr);
    commit(dateStr, draftHour, draftMinute);
  };

  const handleHourChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    setDraftHour(digits);
    if (digits.length === 2 && draftDate) {
      commit(draftDate, digits, draftMinute);
    }
  };

  const handleMinuteChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    setDraftMinute(digits);
    if (digits.length === 2 && draftDate) {
      commit(draftDate, draftHour, digits);
    }
  };

  const normalizeTimeOnBlur = () => {
    const h = pad2(clampInt(Number(draftHour || '0'), 0, 23));
    const mi = pad2(clampInt(Number(draftMinute || '0'), 0, 59));
    setDraftHour(h);
    setDraftMinute(mi);
    if (draftDate) commit(draftDate, h, mi);
  };

  const handleClear = () => {
    setDraftDate('');
    setDraftHour('00');
    setDraftMinute('00');
    onChange('');
    setOpen(false);
  };

  const handleNow = () => {
    const now = new Date();
    const dateStr = toDateValue(now);
    const h = pad2(now.getHours());
    const mi = pad2(now.getMinutes());
    setDraftDate(dateStr);
    setDraftHour(h);
    setDraftMinute(mi);
    setViewMonth(startOfMonth(now));
    onChange(formatDateTimeValue(now));
    setOpen(false);
  };

  const handleConfirm = () => {
    if (!draftDate) return;
    normalizeTimeOnBlur();
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
          open
            ? 'border-primary-400 ring-2 ring-primary-100 bg-background-50'
            : 'border-background-200 bg-background-50 hover:border-background-300'
        }`}
      >
        <i className="ri-calendar-schedule-line text-foreground-400 shrink-0" />
        <span
          className={`flex-1 min-w-0 truncate ${
            displayText ? 'text-foreground-800' : 'text-foreground-400'
          }`}
        >
          {displayText || placeholder}
        </span>
        {clearable && hasValue ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) handleClear();
              }
            }}
            className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-200 shrink-0"
            title="清除"
          >
            <i className="ri-close-circle-fill text-sm" />
          </span>
        ) : (
          <i
            className={`ri-arrow-down-s-line text-foreground-400 shrink-0 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-[120] w-[19.5rem] rounded-xl border border-background-200 bg-background-50 shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-background-100">
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, -1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              title="上个月"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <div className="text-sm font-semibold text-foreground-900">{monthLabel}</div>
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              title="下个月"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>

          <div className="px-3 pt-2 pb-1">
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="h-8 flex items-center justify-center text-[11px] font-medium text-foreground-400"
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {cells.map(({ date, inMonth }) => {
                const dayValue = toDateValue(date);
                const isSelected = !!draftDay && sameDay(date, draftDay);
                const isToday = sameDay(date, today);

                return (
                  <button
                    key={dayValue + String(inMonth)}
                    type="button"
                    onClick={() => handleDayClick(date)}
                    className="relative h-9 flex items-center justify-center text-sm transition-colors"
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                        isSelected
                          ? 'bg-primary-500 text-background-50 font-semibold'
                          : inMonth
                            ? 'text-foreground-800 hover:bg-background-100'
                            : 'text-foreground-300 hover:bg-background-100'
                      } ${!isSelected && isToday ? 'ring-1 ring-primary-300 text-primary-600 font-medium' : ''}`}
                    >
                      {date.getDate()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="px-3 py-2.5 border-t border-background-100 flex items-center gap-2">
            <i className="ri-time-line text-foreground-400 shrink-0" />
            <span className="text-xs text-foreground-500 shrink-0">时间</span>
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                inputMode="numeric"
                value={draftHour}
                onChange={(e) => handleHourChange(e.target.value)}
                onBlur={normalizeTimeOnBlur}
                className="w-11 h-8 text-center text-sm rounded-md border border-background-200 bg-white outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                aria-label="小时"
              />
              <span className="text-foreground-500 font-medium">:</span>
              <input
                type="text"
                inputMode="numeric"
                value={draftMinute}
                onChange={(e) => handleMinuteChange(e.target.value)}
                onBlur={normalizeTimeOnBlur}
                className="w-11 h-8 text-center text-sm rounded-md border border-background-200 bg-white outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                aria-label="分钟"
              />
            </div>
          </div>

          <div className="px-3 py-2 border-t border-background-100 flex items-center justify-between gap-2">
            <p className="text-[11px] text-foreground-400 truncate">
              {draftDate
                ? `${draftDate} ${pad2(clampInt(Number(draftHour || '0'), 0, 23))}:${pad2(clampInt(Number(draftMinute || '0'), 0, 59))}`
                : '请选择日期'}
            </p>
            <div className="flex items-center gap-1.5 shrink-0">
              {clearable && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-2.5 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors"
                >
                  清除
                </button>
              )}
              <button
                type="button"
                onClick={handleNow}
                className="px-2.5 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors"
              >
                此刻
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="px-2.5 py-1 rounded-md text-xs font-medium text-primary-600 hover:bg-primary-50 transition-colors"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
